import { gql, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const DASHBOARD_STATS_QUERY = gql`
    query DashboardStats($startDate: DateTime, $endDate: DateTime) {
        clanStandingsCurrentSeason {
            clanName
            clanTag
            clanColor
            points
        }
        topPlayersCurrentSeason(limit: 5, offset: 0) {
            playerName
            value
            clanTag
            clanColor
        }
        topPlayersShieldValue(limit: 5, offset: 0) {
            playerName
            value
            clanTag
            clanColor
        }
        topBitsGifters(limit: 5) {
            playerName
            value
            clanTag
            clanColor
        }
        topGiftedSubs(limit: 5) {
            playerName
            value
            clanTag
            clanColor
        }
        topSentryPlayers(limit: 5) {
            playerName
            value
            clanTag
            clanColor
        }
        recentTwitchRaiders(limit: 5) {
            raiderUsername
            viewerCount
            raidedAt
        }
        checkinsPerSession(startDate: $startDate, endDate: $endDate) {
            sessionNumber
            sessionDate
            count
        }
        raidsPerSession(startDate: $startDate, endDate: $endDate) {
            sessionNumber
            sessionDate
            count
        }
        sentrysPerSession(startDate: $startDate, endDate: $endDate) {
            sessionNumber
            sessionDate
            count
        }
    }
`;

interface LeaderboardPlayer {
    playerName: string;
    value: number;
    clanTag: string;
    clanColor: string | null;
}

interface ClanStanding {
    clanName: string;
    clanTag: string;
    clanColor: string | null;
    points: number;
}

interface TwitchRaider {
    raiderUsername: string;
    viewerCount: number;
    raidedAt: string;
}

interface SessionStats {
    sessionNumber: number;
    sessionDate: string;
    count: number;
}

interface DashboardData {
    clanStandingsCurrentSeason: ClanStanding[];
    topPlayersCurrentSeason: LeaderboardPlayer[];
    topPlayersShieldValue: LeaderboardPlayer[];
    topBitsGifters: LeaderboardPlayer[];
    topGiftedSubs: LeaderboardPlayer[];
    topSentryPlayers: LeaderboardPlayer[];
    recentTwitchRaiders: TwitchRaider[];
    checkinsPerSession: SessionStats[];
    raidsPerSession: SessionStats[];
    sentrysPerSession: SessionStats[];
}

function StatCard({
    title,
    data,
    valueLabel,
}: {
    title: string;
    data: LeaderboardPlayer[];
    valueLabel: string;
}) {
    if (!data || data.length === 0) {
        return (
            <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
                <h3 className="text-brand mb-4 text-lg font-semibold">
                    {title}
                </h3>
                <div className="text-brand/60 py-8 text-center">
                    No data available
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
            <h3 className="text-brand mb-4 text-lg font-semibold">{title}</h3>
            <div className="space-y-3">
                {data.map((player, idx) => (
                    <div
                        key={idx}
                        className="flex items-center justify-between rounded border border-slate-700 bg-slate-900 p-3"
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-brand/60 text-2xl font-bold">
                                #{idx + 1}
                            </span>
                            <div>
                                <div className="text-brand font-semibold">
                                    {player.playerName}
                                </div>
                                <div
                                    className="text-sm"
                                    style={{
                                        color: player.clanColor || "#94a3b8",
                                    }}
                                >
                                    {player.clanTag}
                                </div>
                            </div>
                        </div>
                        <div className="text-brand text-xl font-bold">
                            {player.value.toLocaleString()} {valueLabel}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ClanStandingsCard({ data }: { data: ClanStanding[] }) {
    if (!data || data.length === 0) {
        return (
            <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
                <h3 className="text-brand mb-4 text-lg font-semibold">
                    Clan Standings (Current Season)
                </h3>
                <div className="text-brand/60 py-8 text-center">
                    No data available
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
            <h3 className="text-brand mb-4 text-lg font-semibold">
                Clan Standings (Current Season)
            </h3>
            <div className="space-y-3">
                {data.map((clan, idx) => (
                    <div
                        key={idx}
                        className="flex items-center justify-between rounded border border-slate-700 bg-slate-900 p-3"
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-brand/60 text-2xl font-bold">
                                #{idx + 1}
                            </span>
                            <div>
                                <div className="text-brand font-semibold">
                                    {clan.clanName}
                                </div>
                                <div
                                    className="text-sm"
                                    style={{
                                        color: clan.clanColor || "#94a3b8",
                                    }}
                                >
                                    {clan.clanTag}
                                </div>
                            </div>
                        </div>
                        <div className="text-brand text-xl font-bold">
                            {clan.points.toLocaleString()} VP
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TwitchRaidersCard({ data }: { data: TwitchRaider[] }) {
    if (!data || data.length === 0) {
        return (
            <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
                <h3 className="text-brand mb-4 text-lg font-semibold">
                    Recent Twitch Raiders
                </h3>
                <div className="text-brand/60 py-8 text-center">
                    No raids yet
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
            <h3 className="text-brand mb-4 text-lg font-semibold">
                Recent Twitch Raiders
            </h3>
            <div className="space-y-3">
                {data.map((raid, idx) => (
                    <div
                        key={idx}
                        className="flex items-center justify-between rounded border border-slate-700 bg-slate-900 p-3"
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-brand text-2xl">📯</span>
                            <div>
                                <div className="text-brand font-semibold">
                                    {raid.raiderUsername}
                                </div>
                                <div className="text-brand/60 text-sm">
                                    {new Date(
                                        raid.raidedAt,
                                    ).toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                        <div className="text-brand text-xl font-bold">
                            {raid.viewerCount.toLocaleString()} viewers
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ChartCard({
    title,
    data,
    dataKey,
    color,
}: {
    title: string;
    data: SessionStats[];
    dataKey: string;
    color: string;
}) {
    if (!data || data.length === 0) {
        return (
            <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
                <h3 className="text-brand mb-4 text-lg font-semibold">
                    {title}
                </h3>
                <div className="text-brand/60 py-8 text-center">
                    No data available
                </div>
            </div>
        );
    }

    const chartData = data.map((stat) => ({
        date: new Date(stat.sessionDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        }),
        [dataKey]: stat.count,
    }));

    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
            <h3 className="text-brand mb-4 text-lg font-semibold">{title}</h3>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis
                        dataKey="date"
                        stroke="#94a3b8"
                        tick={{ fontSize: 12 }}
                    />
                    <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #475569",
                            borderRadius: "8px",
                            color: "#e2e8f0",
                        }}
                    />
                    <Legend wrapperStyle={{ color: "#e2e8f0" }} />
                    <Line
                        type="monotone"
                        dataKey={dataKey}
                        stroke={color}
                        strokeWidth={2}
                        dot={{ fill: color, r: 4 }}
                        activeDot={{ r: 6 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export default function LonghousePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateRange, setDateRange] = useState<string>(
        searchParams.get("range") || "30",
    );

    // Update URL when date range changes
    useEffect(() => {
        setSearchParams({ range: dateRange }, { replace: true });
    }, [dateRange, setSearchParams]);

    // Memoize start date to prevent recalculation on every render
    const startDate = useMemo(() => {
        if (dateRange === "all") return null;
        const days = parseInt(dateRange);
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date.toISOString();
    }, [dateRange]);

    const { data, loading, error } = useQuery<DashboardData>(
        DASHBOARD_STATS_QUERY,
        {
            variables: {
                startDate,
                endDate: null,
            },
            fetchPolicy: "network-only",
            notifyOnNetworkStatusChange: false,
        },
    );

    // Log errors to console for debugging
    if (error) {
        console.error("Dashboard query error:", error);
        console.error("GraphQL errors:", error.graphQLErrors);
        console.error("Network error:", error.networkError);
    }

    if (loading && !data) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-brand text-xl">Loading dashboard...</div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-4">
                <div className="text-xl text-red-500">
                    Error loading dashboard
                </div>
                <div className="text-brand/60 max-w-2xl text-center text-sm">
                    {error.message}
                </div>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-brand hover:bg-brand/80 rounded px-4 py-2 text-white"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!data) {
        return null;
    }

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-brand text-3xl font-bold">
                    Longhouse Dashboard
                </h1>

                {/* Date Range Filter */}
                <div className="flex items-center gap-4">
                    <label className="text-brand text-sm font-medium">
                        Graph Time Range:
                    </label>
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="bg-brand_charcoal border-brand text-brand focus:ring-brand rounded-lg border px-4 py-2 focus:ring-1 focus:outline-none"
                    >
                        <option value="7">Last 7 Days</option>
                        <option value="14">Last 14 Days</option>
                        <option value="30">Last 30 Days</option>
                        <option value="60">Last 60 Days</option>
                        <option value="90">Last 90 Days</option>
                        <option value="180">Last 6 Months</option>
                        <option value="365">Last Year</option>
                        <option value="all">All Time</option>
                    </select>
                </div>
            </div>

            {/* Top Stats Grid */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <ClanStandingsCard
                    data={data.clanStandingsCurrentSeason || []}
                />
                <StatCard
                    title="Top 5 VP (Current Season)"
                    data={data.topPlayersCurrentSeason || []}
                    valueLabel="VP"
                />
                <StatCard
                    title="Top 5 Shield Value"
                    data={data.topPlayersShieldValue || []}
                    valueLabel="SV"
                />
                <StatCard
                    title="Top 5 Bits Gifters"
                    data={data.topBitsGifters || []}
                    valueLabel="bits"
                />
                <StatCard
                    title="Top 5 Gifted Subs"
                    data={data.topGiftedSubs || []}
                    valueLabel="subs"
                />
                <StatCard
                    title="Top 5 Most Active Sentry Players"
                    data={data.topSentryPlayers || []}
                    valueLabel="sentrys"
                />
            </div>

            {/* Raiders Card */}
            <TwitchRaidersCard data={data.recentTwitchRaiders || []} />

            {/* Charts Grid */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <ChartCard
                    title="Checkins Per Session"
                    data={data.checkinsPerSession || []}
                    dataKey="checkins"
                    color="#10b981"
                />
                <ChartCard
                    title="Raids Per Raid Session"
                    data={data.raidsPerSession || []}
                    dataKey="raids"
                    color="#3b82f6"
                />
            </div>

            <div className="grid grid-cols-1 gap-6">
                <ChartCard
                    title="Sentrys Per Session"
                    data={data.sentrysPerSession || []}
                    dataKey="sentrys"
                    color="#f59e0b"
                />
            </div>
        </div>
    );
}
